import $ from 'jquery';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty, noop, result } from 'underscore';
import { DefaultButton, FontIcon, Label, Link, PrimaryButton, Panel, PanelType } from '@fluentui/react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useBoolean } from '@fluentui/react-hooks';
import { Alert } from '@bingads-webui-react/alert';
import { AudienceGroup } from '@bingads-webui/campaign-odata-schemas';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { ErrorMessages, ValidationGroup } from '@bingads-webui-react/validation';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { withScope } from '@bingads-webui-react/scope-service';
import { withSiteMap, getRoutePathById } from '@bingads-webui-react/with-site-map';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import * as gridColumn from '@bingads-webui-component-legacy/bingads-grid/shared/column';
import odata from '@bingads-webui-component-legacy/http/channel/odata';

import { AudienceSection } from './audience-section';
import { DemographicsSection } from './demographics-section';
import { SaveDialog } from './save-dialog';
import { getAudienceEditorStyle } from '../styles/audience-editor';
import { initialAudienceGroupViewModel, modelToViewModelForAudienceGroup, viewModelToModelForAudienceGroup, saveAudienceGroup } from '../model/audience-group';
import { withoutEasyDismiss } from './without-easy-dismiss';

const i18nLegacy = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/audience-campaign');

const UnstyledAudienceEditor = (props) => {
  const {
    audienceGroup,
    originalAudienceGroup,
    isReadOnlyUser,
    classes,
    i18n,
    isOpen,
    onAudienceGroupChanged,
    onClose,
    onDismiss,
    onGenericError,
    permissions,
    scope,
    siteMap,
    wizardPerfMarker,
  } = props;

  const serverContext = useContext(ServerContext);
  const audienceCampaignPageContext = {
    CurrentAccount: {
      Id: serverContext.CurrentAccount.Id,
    },
    CurrentCustomerId: serverContext.CurrentCustomer.Id,
  };

  DependencyResolver.set('component/i18n/loader!campaignui/feature/audience-campaign', i18nLegacy);
  DependencyResolver.set('component/bingads-grid/shared/column', gridColumn.bingadsGridColumn);
  DependencyResolver.set('component/js-data-campaign/index', jsdata);
  DependencyResolver.set('audienceCampaignPageContext', audienceCampaignPageContext);
  DependencyResolver.set('component/http/channel/odata', odata);

  const [viewModel, setViewModel] = useState(audienceGroup ? modelToViewModelForAudienceGroup(audienceGroup) : initialAudienceGroupViewModel);

  const [customerData] = useState(result(viewModel.data, 'CustomerData', []));

  const [interests] = useState(result(viewModel.data, 'Interests', []));

  const [ageRanges] = useState(result(viewModel.data, 'AgeRanges', []));

  const [genders] = useState(result(viewModel.data, 'GenderTypes', []));

  const [companyName] = useState(result(viewModel.data, 'companyNameTargets', []));

  const [industry] = useState(result(viewModel.data, 'industry', []));

  const [jobFunction] = useState(result(viewModel.data, 'jobTargets', []));

  const [serverErrors, setServerErrors] = useState([]);

  const [alwaysShowError, setAlwaysShowError] = useState(false);

  const [showSaveDialog, { toggle: toggleShowSaveDialog }] = useBoolean(false);

  const { AgeGenderDisclaimer } = result(permissions, 'dynamic', {});

  const onValidate = useCallback((results) => {
    setViewModel({
      ...results,
      data: {
        ...viewModel.data,
        ...results.data,
      },
    });
  }, [viewModel.data]);

  const onSaveInPlace = useCallback(() => {
    saveAudienceGroup(viewModel, i18n)
      .then(response => onAudienceGroupChanged(viewModelToModelForAudienceGroup(response)))
      .then(onClose)
      .catch((response) => {
        setServerErrors(response.errors);
      })
      .finally(withUiBlock($('.audience-editor .ms-Panel-main')));
  }, [viewModel, i18n, onAudienceGroupChanged, onClose]);

  const onSave = useCallback(() => {
    setAlwaysShowError(true);
    if (!viewModel.isValid) {
      return;
    }

    if (audienceGroup && audienceGroup.AssociationsCount > 0 &&
      !(audienceGroup.AssociationsCount === 1 && originalAudienceGroup && originalAudienceGroup.Id === audienceGroup.Id)) {
      toggleShowSaveDialog(true);
    } else {
      onSaveInPlace();
    }
  }, [audienceGroup, onSaveInPlace, originalAudienceGroup, toggleShowSaveDialog, viewModel.isValid]);

  const onRenderFooterContent = useCallback(() => (
    <div className="audience-editor-buttons">
      <PrimaryButton className="btn-save" disabled={isReadOnlyUser} onClick={onSave}>
        {i18n.getString(_TL_('Save'))}
      </PrimaryButton>
      <DefaultButton className="btn-cancel" onClick={onClose}>
        {i18n.getString(_TL_('Cancel'))}
      </DefaultButton>
    </div>
  ), [i18n, onClose, onSave, isReadOnlyUser]);

  const onGlobalShortcut = useCallback((e) => {
    if (e.key === 'Tab' && !e.shiftKey && e.target.parentElement.className.includes('nav-skip')) {
      e.preventDefault();
      document.getElementsByClassName('ms-Panel-closeButton')[0].focus();
    }
    if (e.key === 'Tab' && e.shiftKey && e.target.className.includes('HelpTopic analysable')) {
      e.preventDefault();
      document.getElementsByClassName('btn-cancel')[0].focus();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keyup', onGlobalShortcut);
    return () => {
      document.removeEventListener('keyup', onGlobalShortcut);
    };
  }, [i18n, onGlobalShortcut]);

  const onNavigateToAudiencesPage = useCallback((e) => {
    e.preventDefault();

    const route = getRoutePathById(siteMap, 'audiences-library');
    scope.remove(['campaignId', 'assetGroupId']);
    scope.save(route);
  }, [scope, siteMap]);

  return (
    <>
      <Panel
        className={classNames(['audience-editor', classes.editorContainer])}
        headerText={isEmpty(audienceGroup) ? i18n.getString(_TL_('Create audience signal')) : i18n.getString(_TL_('Edit audience signal'))}
        closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
        isOpen={isOpen}
        onDismiss={onDismiss}
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom
        type={PanelType.medium}
      >
        <FluentProvider theme={webLightTheme}>
          <Alert
            level="Info"
            dismissible={false}
            icon={(<FontIcon iconName="Info" />)}
            message={i18n.getString(_TL_('To reach more customers and maximize conversions, please note that your ads may surface to audiences beyond those associated with your audience signal.'))}
          />
          {serverErrors && (
            <div className="server-errors">
              <ErrorMessages messages={serverErrors} />
            </div>
          )}
          <ValidationGroup validationProps={{
            data: viewModel.data,
            schema: AudienceGroup,
            onValidate,
            stopPropagation: true,
          }}
          >
            <div className="audience-signal-section">
              <Label>
                {i18n.getString(_TL_('Name'))}
                <HelpPopup aria-label={i18n.getString(_TL_('Name'))} topic="pop_BA_PMAX_AudSignalName" />
              </Label>
              <RemainingCharsValidatedTextField
                className="input-audience-signal-name"
                i18n={i18n}
                placeholder={i18n.getString(_TL_('Enter a name for your audience signal'))}
                validationProps={{
                  field: 'Name',
                  schema: AudienceGroup,
                  alwaysShowError,
                }}
              />
            </div>
            <div className="audience-signal-section interests-section">
              <AudienceSection
                i18n={i18n}
                title={i18n.getString(_TL_('Interests'))}
                descritpion={i18n.getString(_TL_('Reach customers who have shown purchase intent signals within a particular category.'))}
                addLabel={i18n.getString(_TL_('Add interests'))}
                addPanelTitle={i18n.getString(_TL_('Add interests'))}
                addPanelDescription={i18n.getString(_TL_('Reach customers who have shown purchase intent signals within a particular category.'))}
                audienceType="InMarket"
                removeLeadingSlash
                onGenericError={onGenericError}
                validationProps={{ field: 'Interests', value: interests }}
                wizardPerfMarker={wizardPerfMarker}
              />
            </div>
            <div className="audience-signal-section cutomer-data-section">
              <AudienceSection
                i18n={i18n}
                title={i18n.getString(_TL_('Your first-party data'))}
                descritpion={i18n.getString(_TL_('Target people who previously interacted with your website and products.'))}
                addLabel={i18n.getString(_TL_('Add audience list'))}
                addPanelTitle={i18n.getString(_TL_('Add audience list'))}
                addPanelDescription={
                  <ReactTemplate
                    model={{
                      link: text => (<Link href={getRoutePathById(siteMap, 'audiences-library')} onClick={onNavigateToAudiencesPage} underline>{text}</Link>),
                    }}
                    template={i18n.getString(_TL_('Choose from your existing audience lists. To create a new list, go to {{link}}Audiences{{/link}}.'))}
                  />
                }
                onGenericError={onGenericError}
                validationProps={{ field: 'CustomerData', value: customerData }}
                popupId="pop_BA_PMAX_AudienceLists"
                wizardPerfMarker={wizardPerfMarker}
              />
            </div>
            <div className="audience-signal-section demographics-section">
              <DemographicsSection
                i18n={i18n}
                ageRanges={ageRanges}
                genders={genders}
                companyName={companyName}
                industry={industry}
                jobFunction={jobFunction}
              />
            </div>
          </ValidationGroup>
          {AgeGenderDisclaimer && <Alert
            message={i18n.getString(_TL_('If you’re advertising in connection with any financial, insurance, education, career and employment, and/or housing services, you should <b>not</b> use individuals’ demographics—such as age, gender, location, etc.—for the purpose of personalizing advertising, segmenting, or profiling customers.'))}
            level="Info"
            icon={(<div className="alert-image" />)}
            dismissible={false}
          />}
        </FluentProvider>
      </Panel>
      {showSaveDialog && <SaveDialog
        audienceGroupViewModel={cloneDeep(viewModel)}
        i18n={i18n}
        isOpen={showSaveDialog}
        onAudienceGroupChanged={onAudienceGroupChanged}
        onClose={onClose}
        onDismiss={toggleShowSaveDialog}
        onGenericError={onGenericError}
        onSaveInPlace={onSaveInPlace}
        scope={scope}
        siteMap={siteMap}
      />}
    </>
  );
};

UnstyledAudienceEditor.propTypes = {
  audienceGroup: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
    Dimensions: PropTypes.arrayOf(PropTypes.shape({
      Type: PropTypes.string.isRequired,
    })).isRequired,
    AssociationsCount: PropTypes.number,
  }),
  originalAudienceGroup: PropTypes.shape({
    Id: PropTypes.number.isRequired,
  }),
  isReadOnlyUser: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    editorContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isOpen: PropTypes.bool,
  onAudienceGroupChanged: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onGenericError: PropTypes.func,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
    }).isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    remove: PropTypes.func,
    save: PropTypes.func,
  }).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  wizardPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
};

UnstyledAudienceEditor.defaultProps = {
  isOpen: false,
  audienceGroup: undefined,
  originalAudienceGroup: undefined,
  onAudienceGroupChanged: noop,
  onGenericError: noop,
};

export const AudienceEditor = withDefaultStyles(withScope(withSiteMap(withoutEasyDismiss(UnstyledAudienceEditor))), getAudienceEditorStyle);
